import {
  BLANK_H,
  BLOCK_GAP,
  CALLOUT_PAD,
  DIVIDER_H,
  DOC_ICON_SIZE,
  DOC_TITLE_H,
  HEADING_H,
  LINE_STEP,
  MEDIA_MAX_H,
  MEDIA_SCALE,
} from './constants';

/**
 * Lays out nodes starting at mini-Y `y`; pushes render items and
 * (docY → miniY) anchor pairs. Returns the Y after the last node.
 */
export const layoutNodes = (nodes, y, items, anchors) => {
  nodes.forEach((node, idx) => {
    if (idx > 0) y += BLOCK_GAP;
    anchors?.push({ doc: node.docTop, mini: y });

    if (node.variant === 'row') {
      let maxEnd = y;
      node.columns.forEach((col) => {
        // NO anchors inside rows: columns lay out in parallel, so their
        // children's (doc → mini) pairs overlap and would make the map
        // non-monotonic — the cause of viewport drags "sticking"
        const end = layoutNodes(col.children, y, items, null);
        if (end > maxEnd) maxEnd = end;
      });
      y = maxEnd;
    } else if (node.variant === 'choice') {
      const tabH = LINE_STEP;
      const innerStart = y + CALLOUT_PAD + tabH;
      const innerEnd = node.children.length
        ? layoutNodes(node.children, innerStart, items, anchors)
        : innerStart + LINE_STEP;
      const boxH = innerEnd + CALLOUT_PAD - y;
      items.push({ ...node, miniTop: y, miniH: boxH, boxBefore: items.length });
      y += boxH;
    } else if (node.variant === 'callout') {
      const innerStart = y + CALLOUT_PAD;
      const innerEnd = node.children.length
        ? layoutNodes(node.children, innerStart, items, anchors)
        : innerStart + LINE_STEP;
      const boxH = innerEnd + CALLOUT_PAD - y;
      // The box is inserted BEFORE its children so it paints underneath
      items.push({ ...node, miniTop: y, miniH: boxH, boxBefore: items.length });
      y += boxH;
    } else if (node.variant === 'text' || node.variant === 'checkbox') {
      const h = node.lines * LINE_STEP - 1; // no gap after the last line
      items.push({ ...node, miniTop: y, miniH: h });
      y += h;
    } else if (node.variant === 'blank') {
      items.push({ ...node, miniTop: y, miniH: BLANK_H });
      y += BLANK_H;
    } else if (node.variant === 'heading') {
      items.push({ ...node, miniTop: y, miniH: HEADING_H });
      y += HEADING_H;
    } else if (node.variant === 'doc-icon') {
      items.push({ ...node, miniTop: y, miniH: DOC_ICON_SIZE });
      y += DOC_ICON_SIZE + 1;
    } else if (node.variant === 'doc-title') {
      items.push({ ...node, miniTop: y, miniH: DOC_TITLE_H });
      y += DOC_TITLE_H + 1;
    } else if (node.variant === 'divider') {
      items.push({ ...node, miniTop: y, miniH: DIVIDER_H });
      y += DIVIDER_H;
    } else if (node.variant === 'toggle') {
      // Header line, then (when open) the children below it
      items.push({ ...node, miniTop: y, miniH: LINE_STEP });
      y += LINE_STEP;
      if (node.open && node.children.length) {
        y = layoutNodes(node.children, y + 1, items, anchors);
      }
    } else if (node.variant === 'code') {
      const h = CALLOUT_PAD * 2 + node.lines * LINE_STEP - 1;
      items.push({ ...node, miniTop: y, miniH: h });
      y += h;
    } else if (node.variant === 'table') {
      // Chrome pad around the grid so the mini card matches the real table
      // surface (header + rows + add-row), same as the variables block.
      const inner = Math.max(LINE_STEP * 3, (node.lines || 1) * LINE_STEP - 1);
      const h = CALLOUT_PAD * 2 + inner;
      items.push({ ...node, miniTop: y, miniH: h });
      y += h;
    } else if (node.variant === 'variables') {
      // Chrome pad around the grid so the mini box matches the real block's
      // padded surface (label + header + rows), not a flat table strip.
      const inner = Math.max(LINE_STEP * 3, (node.lines || 1) * LINE_STEP - 1);
      const h = CALLOUT_PAD * 2 + inner;
      items.push({ ...node, miniTop: y, miniH: h });
      y += h;
    } else if (node.variant === 'media') {
      const h = Math.max(4, Math.min(MEDIA_MAX_H, node.docHeight * MEDIA_SCALE));
      items.push({ ...node, miniTop: y, miniH: h });
      y += h;
    }
    anchors?.push({ doc: node.docBottom, mini: y });
  });
  return y;
};

/** Piecewise-linear interpolation through sorted anchor pairs. */
export const interpolate = (pairs, key, outKey, value) => {
  if (pairs.length === 0) return 0;
  if (value <= pairs[0][key]) return pairs[0][outKey];
  for (let i = 1; i < pairs.length; i += 1) {
    if (value <= pairs[i][key]) {
      const a = pairs[i - 1];
      const b = pairs[i];
      const span = b[key] - a[key];
      const t = span > 0 ? (value - a[key]) / span : 0;
      return a[outKey] + t * (b[outKey] - a[outKey]);
    }
  }
  return pairs[pairs.length - 1][outKey];
};
